import { useEffect, useState } from 'react';
import { supabase } from '../../../../../utils/supabaseClient';

/**
 * Fetch artworks for a collection using mapping-first, with a simple fallback to vcs.collection_id.
 * Supported filters: show = submitted | accepted | all
 * Supported sort: display (by mapping.display_order), newest (by mapping created_at if present)
 */
export function useCollectionArtworks(
  collectionId,
  { show = 'submitted', sort = 'display', page = 1, pageSize = 24 } = {}
) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!collectionId) return;
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 useCollectionArtworks: Loading artworks for collection_id:', collectionId);
        console.log('🔍 useCollectionArtworks: Filters - show:', show, 'sort:', sort, 'page:', page, 'pageSize:', pageSize);

        // Count query for total with filters applied (head only)
        let countQuery = supabase
          .from('collection_vc_mapping')
          .select('vc_id', { count: 'exact', head: true })
          .eq('collection_id', collectionId);

        if (show === 'accepted') {
          countQuery = countQuery.eq('accepted', true);
        }
        // For 'submitted' or 'all', don't add any accepted filter - show everything

        const { count: totalCount, error: countError } = await countQuery;
        if (countError) throw countError;

        console.log('🔍 useCollectionArtworks: Count query result - totalCount:', totalCount);

        // Data query with nested vcs -> users
        let dataQuery = supabase
          .from('collection_vc_mapping')
          .select(
            `
              vc_id,
              accepted,
              order_num,
              created_at,
              vcs (
                vc_id,
                title,
                cover_image,
                created_at,
                uuid,
                width,
                height,
                depth,
                price,
                users (
                  uuid,
                  first_name,
                  last_name,
                  nickname
                )
              )
            `
          )
          .eq('collection_id', collectionId);

        if (show === 'accepted') {
          dataQuery = dataQuery.eq('accepted', true);
        }
        // For 'submitted' or 'all', don't add any accepted filter - show everything

        // Sorting - mirror legacy: order_num asc, then mapping created_at desc for newest
        if (sort === 'display') {
          dataQuery = dataQuery.order('order_num', { ascending: true, nullsFirst: true });
        } else if (sort === 'newest') {
          dataQuery = dataQuery.order('created_at', { ascending: false });
        }

        // Server-side pagination
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;
        dataQuery = dataQuery.range(from, to);

        const { data, error: dataError } = await dataQuery;
        if (dataError) throw dataError;

        console.log('🔍 useCollectionArtworks: Data query result - rows:', data?.length, 'data:', data);

        const mapped = (data || []).map((row) => ({
          vc_id: row.vc_id,
          accepted: row.accepted,
          display_order: row.order_num ?? null,
          mapping_created_at: row.created_at,
          title: row.vcs?.title || 'Untitled',
          cover_image: row.vcs?.cover_image || null,
          width: row.vcs?.width ?? null,
          height: row.vcs?.height ?? null,
          depth: row.vcs?.depth ?? null,
          price: row.vcs?.price ?? null,
          artist: row.vcs?.users
            ? {
                uuid: row.vcs.users.uuid,
                nickname: row.vcs.users.nickname,
                first_name: row.vcs.users.first_name,
                last_name: row.vcs.users.last_name,
              }
            : null,
        }));

        if (!ignore) {
          setItems(mapped);
          setTotal(totalCount || 0);
        }
      } catch (e) {
        if (!ignore) setError(e);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, [collectionId, show, sort, page, pageSize]);

  return { items, total, page, pageSize, loading, error };
}
